/**
 * Persist and load Support at Home (home_care) saved strategies for Optimise Your Plan + Service Builder.
 */

import { applyStrategyToFactFind } from '../../strategy/SupportAtHomeStrategyApplicator.ts';
import {
    applyRadFundingToFactFind,
    extractAgedCareIntegrationDetails,
} from '../../strategy/StrategyIntegrationUtils.ts';
import { getAgedCareModellerResidencyOverrides } from '../../../../shared/src/calculation/householdStatus.ts';
import * as calculationApi from '../../services/calculationApi.ts';
import {
    HOME_CARE_STRATEGIES_KEY,
    ACTIVE_HOME_CARE_STRATEGY_ID_KEY,
    HOME_CARE_STRATEGIES_UPDATED_EVENT,
    SERVICE_BUILDER_HC_PREFIX,
    SERVICE_BUILDER_AC_PREFIX,
    notifyHomeCareStrategiesUpdated,
    setActiveHomeCareStrategyId,
    formatServiceBuilderSelectionId,
} from '../../strategy/careCompanionStrategyEvents.ts';
import type { ServiceBuilderSelectionKind } from '../../strategy/careCompanionStrategyEvents.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { SavedStrategy, AgedCareIntegrationDetails } from '../../strategy/StrategyIntegrationUtils.ts';
import type { StrategyCandidate } from '../../strategy/SupportAtHomeStrategyApplicator.ts';
import type { EvaluatedStrategy } from '../../strategy/supportAtHomeRecommendedStrategies.ts';
import type { FactFind } from '../../../../shared/src/calculation/factFindTypes.ts';

export {
    HOME_CARE_STRATEGIES_KEY,
    ACTIVE_HOME_CARE_STRATEGY_ID_KEY,
    HOME_CARE_STRATEGIES_UPDATED_EVENT,
    SERVICE_BUILDER_HC_PREFIX,
    SERVICE_BUILDER_AC_PREFIX,
    notifyHomeCareStrategiesUpdated,
    setActiveHomeCareStrategyId,
    formatServiceBuilderSelectionId,
};

/** Calc-engine `financials` result bag — free-form (see calculationApi.ts's own `Financials = Record<string, unknown>`). */
type Financials = Record<string, unknown>;

export interface ServiceBuilderSelectionId {
    kind: ServiceBuilderSelectionKind;
    rawId: string | null;
}

export function getAllSavedStrategies(): SavedStrategy[] {
    if (typeof window === 'undefined') return [];
    try {
        const parsed = JSON.parse(window.sessionStorage.getItem(HOME_CARE_STRATEGIES_KEY) || '[]');
        if (!Array.isArray(parsed)) return [];
        // Heal duplicates by (type, id), keeping the LAST occurrence — a strategy re-persisted with
        // more data (e.g. its soaSnapshot) supersedes the first write. Duplicate ids rendered as
        // React list keys are exactly the "two children with the same key" warning an adviser hit
        // after the SOA flow persisted a strategy twice through the old append-only persist.
        const byKey = new Map<string, SavedStrategy>();
        for (const strategy of parsed) {
            byKey.set(`${strategy?.type ?? ''}:${strategy?.id ?? ''}`, strategy);
        }
        return Array.from(byKey.values());
    } catch {
        return [];
    }
}

export function getSavedHomeCareStrategies(): SavedStrategy[] {
    return getAllSavedStrategies().filter((s) => s?.type === 'home_care');
}

export function getSavedAgedCareStrategies(): SavedStrategy[] {
    return getAllSavedStrategies().filter((s) => s?.type === 'aged_care');
}

export function parseServiceBuilderSelectionId(selectionId: string | null | undefined): ServiceBuilderSelectionId {
    if (!selectionId || selectionId === 'baseline') {
        return { kind: 'baseline', rawId: null };
    }
    if (selectionId.startsWith(SERVICE_BUILDER_HC_PREFIX)) {
        return { kind: 'home_care', rawId: selectionId.slice(SERVICE_BUILDER_HC_PREFIX.length) };
    }
    if (selectionId.startsWith(SERVICE_BUILDER_AC_PREFIX)) {
        return { kind: 'aged_care', rawId: selectionId.slice(SERVICE_BUILDER_AC_PREFIX.length) };
    }
    return { kind: 'home_care', rawId: selectionId };
}

export function formatAgedCareStrategyLabel(strategy: SavedStrategy | null | undefined): string {
    if (!strategy?.name) return 'Unnamed aged care scenario';
    const sourceTag = strategy.source === 'suggestion_engine' ? ' · Suggestion engine' : '';
    return `${strategy.name}${sourceTag}`;
}

export function getActiveHomeCareStrategyId(): string | null {
    if (typeof window === 'undefined') return null;
    return window.sessionStorage.getItem(ACTIVE_HOME_CARE_STRATEGY_ID_KEY);
}

export function persistHomeCareStrategy(strategy: SavedStrategy): SavedStrategy {
    // UPSERT by (type, id), never append blindly: re-persisting a strategy (e.g. to attach its
    // soaSnapshot after generating the Digital SOA) must replace the record, not duplicate it.
    const all = getAllSavedStrategies();
    const index = all.findIndex((s) => String(s?.id) === String(strategy.id) && s?.type === strategy.type);
    if (index >= 0) all[index] = strategy;
    else all.push(strategy);
    if (typeof window !== 'undefined') {
        window.sessionStorage.setItem(HOME_CARE_STRATEGIES_KEY, JSON.stringify(all));
    }
    setActiveHomeCareStrategyId(formatServiceBuilderSelectionId('home_care', strategy.id));
    notifyHomeCareStrategiesUpdated();
    return strategy;
}

export function resolveInitialServiceBuilderStrategyId(
    homeCareStrategies: SavedStrategy[] = getSavedHomeCareStrategies(),
    agedCareStrategies: SavedStrategy[] = getSavedAgedCareStrategies()
): string {
    return resolveInitialServiceBuilderSelectionId(homeCareStrategies, agedCareStrategies);
}

export function resolveInitialServiceBuilderSelectionId(
    homeCareStrategies: SavedStrategy[] = getSavedHomeCareStrategies(),
    agedCareStrategies: SavedStrategy[] = getSavedAgedCareStrategies()
): string {
    const activeId = getActiveHomeCareStrategyId();
    if (!activeId || activeId === 'baseline') return 'baseline';

    const parsed = parseServiceBuilderSelectionId(activeId);
    if (parsed.kind === 'aged_care' && agedCareStrategies.some((s) => s.id === parsed.rawId)) {
        return formatServiceBuilderSelectionId('aged_care', parsed.rawId);
    }
    if (parsed.kind === 'home_care' && homeCareStrategies.some((s) => s.id === parsed.rawId)) {
        return formatServiceBuilderSelectionId('home_care', parsed.rawId);
    }
    if (agedCareStrategies.some((s) => s.id === activeId)) {
        return formatServiceBuilderSelectionId('aged_care', activeId);
    }
    if (homeCareStrategies.some((s) => s.id === activeId)) {
        return formatServiceBuilderSelectionId('home_care', activeId);
    }
    return 'baseline';
}

/**
 * Build household financials for Service Builder from a saved aged_care strategy (RAD + ongoing fees + residency).
 */
export async function buildFinancialsFromAgedCareStrategy(factFind: DraftFactFind, agedCareStrategy: SavedStrategy): Promise<Financials> {
    const integration = extractAgedCareIntegrationDetails(agedCareStrategy);
    if (!integration) return calculationApi.calculateFinancials(factFind);

    let modifiedFactFind: DraftFactFind = structuredClone(factFind);
    if (integration.radFunding) {
        modifiedFactFind = applyRadFundingToFactFind(modifiedFactFind, integration.radFunding);
    }
    if (integration.livingExpenses !== undefined && integration.livingExpenses !== null) {
        modifiedFactFind.expenses = {
            ...modifiedFactFind.expenses,
            livingExpenses: integration.livingExpenses,
        };
    }

    const fromStrategy: Record<string, unknown> = integration.strategyOptions || {};
    const residencyOverrides = {
        ...(fromStrategy.clientResidencyOverride ? { clientResidencyOverride: fromStrategy.clientResidencyOverride } : {}),
        ...(fromStrategy.partnerResidencyOverride ? { partnerResidencyOverride: fromStrategy.partnerResidencyOverride } : {}),
    };
    const strategyOptionsPayload = {
        integratedAgedCareCost: integration.annualCost || 0,
        integratedAgedCareDetails: integration.annualCost > 0 ? integration : null,
        ...(Object.keys(residencyOverrides).length > 0
            ? residencyOverrides
            // DraftFactFind is structurally compatible with FactFind (see
            // types/formDraft.ts); the shared calc-engine fn defensively coerces internally
            // regardless, so this cast is safe (see factFindTypes.ts header comment).
            : getAgedCareModellerResidencyOverrides(factFind as unknown as FactFind)),
    };

    return calculationApi.calculateFinancials(modifiedFactFind, strategyOptionsPayload);
}

/**
 * Clears the manually-linked aged care strategy. Called when the user EXPLICITLY selects
 * "Baseline" in the Service Builder's financial-position dropdown: selecting an aged-care
 * position writes this key (so the rest of the app aligns), and without this clear a later
 * explicit Baseline choice silently kept the aged-care financials - the builder appeared to
 * ignore the change (owner-caught, 2026-08-25). A link made deliberately in Optimise Your Plan
 * still survives ARRIVING at the builder, because arrival doesn't call this - only the explicit
 * dropdown choice does.
 */
export function clearManualAgedCareLink(): void {
    if (typeof window === 'undefined') return;
    window.sessionStorage.removeItem('careCompanionSelectedAgedCareStrategy');
}

function baselineHasManualAgedCareLink(): boolean {
    if (typeof window === 'undefined') return false;
    const s = window.sessionStorage.getItem('careCompanionSelectedAgedCareStrategy');
    return Boolean(s && s.trim() && s !== 'null');
}

// `integration` is deliberately loosely typed (not `AgedCareIntegrationDetails`): the only external
// caller (ServiceBuilderSection.tsx) resolves it through its own locally-declared, structurally
// looser `ServiceBuilderPosition` shape, so this stays permissive and narrows defensively instead.
function getResidencyOverridesFromIntegration(integration: unknown, factFind: DraftFactFind): Record<string, unknown> {
    if (!integration || typeof integration !== 'object') return {};
    const fromStrategy: Record<string, unknown> = (integration as { strategyOptions?: Record<string, unknown> }).strategyOptions || {};
    const linkedOverrides = {
        ...(fromStrategy.clientResidencyOverride ? { clientResidencyOverride: fromStrategy.clientResidencyOverride } : {}),
        ...(fromStrategy.partnerResidencyOverride ? { partnerResidencyOverride: fromStrategy.partnerResidencyOverride } : {}),
    };
    if (Object.keys(linkedOverrides).length > 0) return linkedOverrides;
    // DraftFactFind is structurally compatible with FactFind (see
    // types/formDraft.ts); the shared calc-engine fn defensively coerces internally regardless,
    // so this cast is safe (see factFindTypes.ts header comment).
    return getAgedCareModellerResidencyOverrides(factFind as unknown as FactFind);
}

export interface ServiceBuilderPosition {
    kind: ServiceBuilderSelectionKind;
    strategy: SavedStrategy | null;
    integration: AgedCareIntegrationDetails | null;
    financials: Financials;
    hasLinkedAgedCare: boolean;
}

/**
 * Resolve financial position for Service Builder from selection id.
 */
export async function resolveServiceBuilderPosition(
    selectionId: string | null | undefined,
    factFind: DraftFactFind,
    homeCareStrategies: SavedStrategy[] = getSavedHomeCareStrategies(),
    agedCareStrategies: SavedStrategy[] = getSavedAgedCareStrategies()
): Promise<ServiceBuilderPosition> {
    const { kind, rawId } = parseServiceBuilderSelectionId(selectionId);
    const isPartnered = factFind?.adviceFor === 'couple';

    if (kind === 'baseline') {
        const hasLinkedAgedCare = isPartnered && baselineHasManualAgedCareLink();
        let integration: AgedCareIntegrationDetails | null = null;
        if (hasLinkedAgedCare && typeof window !== 'undefined') {
            try {
                const stored = window.sessionStorage.getItem('careCompanionSelectedAgedCareStrategy');
                integration = stored ? extractAgedCareIntegrationDetails(JSON.parse(stored)) : null;
            } catch {
                integration = null;
            }
        }
        const financials = hasLinkedAgedCare && integration
            ? await buildFinancialsFromAgedCareStrategy(factFind, integration.savedStrategy)
            : await calculationApi.calculateFinancials(factFind);
        return {
            kind: 'baseline',
            strategy: null,
            integration,
            financials,
            hasLinkedAgedCare,
        };
    }

    if (kind === 'home_care') {
        const strategy = homeCareStrategies.find((s) => s.id === rawId) || null;
        const hasLinkedAgedCare = homeCareStrategyHasLinkedAgedCare(strategy);
        let integration: AgedCareIntegrationDetails | null = null;
        if (hasLinkedAgedCare && strategy?.inputs?.selectedAgedCareStrategyId) {
            const linked = agedCareStrategies.find((s) => s.id === strategy.inputs?.selectedAgedCareStrategyId);
            integration = linked ? extractAgedCareIntegrationDetails(linked) : null;
        }
        const financials: Financials = strategy?.outputs?.financials
            ? (strategy.outputs.financials as Financials)
            : (integration
                ? await buildFinancialsFromAgedCareStrategy(factFind, integration.savedStrategy)
                : await calculationApi.calculateFinancials(factFind));
        return {
            kind: 'home_care',
            strategy,
            integration,
            financials,
            hasLinkedAgedCare,
        };
    }

    const strategy = agedCareStrategies.find((s) => s.id === rawId) || null;
    const integration = strategy ? extractAgedCareIntegrationDetails(strategy) : null;
    const financials = strategy
        ? await buildFinancialsFromAgedCareStrategy(factFind, strategy)
        : await calculationApi.calculateFinancials(factFind);
    return {
        kind: 'aged_care',
        strategy,
        integration,
        financials,
        // Person in residential care — SAH package budget is not available for them (couple or single).
        hasLinkedAgedCare: Boolean(strategy),
    };
}

// `position` accepts any object shaped like `{ hasLinkedAgedCare, integration }` (not strictly our own
// `ServiceBuilderPosition`) since ServiceBuilderSection.tsx resolves it through its own, structurally
// looser local type of the same name.
export function getServiceBuilderContributionContextOptions(
    factFind: DraftFactFind,
    position: { hasLinkedAgedCare?: boolean; integration?: unknown } | null | undefined,
): Record<string, unknown> {
    if (!position?.hasLinkedAgedCare || factFind?.adviceFor !== 'couple') return {};
    const overrides = getResidencyOverridesFromIntegration(position.integration, factFind);
    return Object.keys(overrides).length > 0 ? { strategyOptions: overrides } : {};
}

export function formatHomeCareStrategyLabel(strategy: SavedStrategy | null | undefined): string {
    if (!strategy?.name) return 'Unnamed strategy';
    const sourceTag = strategy.source === 'suggestion_engine' ? ' · Suggestion engine' : '';
    return `${strategy.name}${sourceTag}`;
}

/**
 * True only when this home_care save explicitly integrated a linked aged care scenario.
 */
export function homeCareStrategyHasLinkedAgedCare(strategy: SavedStrategy | null | undefined): boolean {
    if (!strategy?.inputs?.selectedAgedCareStrategyId) return false;
    const ctx = strategy.inputs.agedCareContext;
    if (ctx === 'linked') return true;
    if (ctx === 'current') return false;
    // Legacy manual saves: selectedAgedCareStrategyId implies explicit manual integration.
    // Legacy SE saves may have picked up auto-selected AC id — treat as not linked unless flagged.
    if (strategy.source === 'suggestion_engine') return false;
    return true;
}

export interface BuildHomeCareStrategyFromSuggestionEngineParams {
    factFind: DraftFactFind;
    evaluatedStrategy: EvaluatedStrategy;
    name: string;
    selectedAgedCareIntegration?: AgedCareIntegrationDetails | null;
    agedCareResidencyOverrides?: Record<string, unknown>;
    participantType: string;
    nwopCategory: string | null;
    meansDisclosed: boolean;
    previousITCF: number;
    isPartnered: boolean;
    agedCareContext?: 'current' | 'linked';
}

/**
 * Build a home_care record from a suggestion-engine evaluated strategy (applied factFind + financials).
 */
export async function buildHomeCareStrategyFromSuggestionEngine({
    factFind,
    evaluatedStrategy,
    name,
    selectedAgedCareIntegration = null,
    agedCareResidencyOverrides = {},
    participantType,
    nwopCategory,
    meansDisclosed,
    previousITCF,
    isPartnered,
    agedCareContext = 'current',
}: BuildHomeCareStrategyFromSuggestionEngineParams): Promise<SavedStrategy> {
    const config = evaluatedStrategy?.config || evaluatedStrategy;
    const applyLinkedAgedCare = agedCareContext === 'linked' && selectedAgedCareIntegration;
    let modifiedFactFind: DraftFactFind = applyStrategyToFactFind(factFind, config as StrategyCandidate);

    if (applyLinkedAgedCare && selectedAgedCareIntegration?.radFunding) {
        modifiedFactFind = applyRadFundingToFactFind(modifiedFactFind, selectedAgedCareIntegration.radFunding);
    }
    if (applyLinkedAgedCare && selectedAgedCareIntegration?.livingExpenses !== undefined && selectedAgedCareIntegration?.livingExpenses !== null) {
        modifiedFactFind.expenses = {
            ...modifiedFactFind.expenses,
            livingExpenses: selectedAgedCareIntegration.livingExpenses,
        };
    }

    const agedCareAnnualCost = applyLinkedAgedCare ? (selectedAgedCareIntegration?.annualCost || 0) : 0;
    const strategyOptionsPayload = {
        integratedAgedCareCost: agedCareAnnualCost,
        integratedAgedCareDetails: applyLinkedAgedCare && agedCareAnnualCost > 0 ? selectedAgedCareIntegration : null,
        ...(applyLinkedAgedCare ? agedCareResidencyOverrides : {}),
    };

    const financials = await calculationApi.calculateFinancials(modifiedFactFind, strategyOptionsPayload);
    const contributions = await calculationApi.calculateIndividualContributions(
        modifiedFactFind,
        financials,
        participantType,
        nwopCategory,
        meansDisclosed,
        previousITCF,
        0.5,
        { strategyOptions: agedCareResidencyOverrides }
    );

    const contributionData = isPartnered
        ? (contributions?.combined || contributions?.client)
        : contributions?.client;

    const snap = evaluatedStrategy?.snapshot || {};

    return {
        id: `strategy-${Date.now()}`,
        name: name.trim(),
        type: 'home_care',
        source: 'suggestion_engine',
        inputs: {
            source: 'suggestion_engine',
            seConfig: config,
            agedCareContext,
            selectedAgedCareStrategyId: applyLinkedAgedCare
                // `strategyId` is a legacy/dead fallback field never actually set on AgedCareIntegrationDetails.
                ? (((selectedAgedCareIntegration as unknown as { strategyId?: string })?.strategyId) || selectedAgedCareIntegration?.id || null)
                : null,
            strategyName: evaluatedStrategy.name,
        },
        outputs: {
            annualContribution: contributionData?.contributionAmounts?.totalAnnual ?? snap.contributionAmount ?? 0,
            agePension: financials?.socialSecurity ?? snap.pension ?? 0,
            cashFlow: financials?.cashFlow ?? snap.netCashflow ?? snap.cashflow ?? 0,
            legacyEstimate: snap.legacyValue ?? 0,
            financials,
            contributionRates: contributionData?.contributionRates || {},
            seSnapshot: snap,
        },
        createdAt: new Date().toISOString(),
    };
}
